// ──────────────────────────────────────────────────────────────────────────
//  Myth-track derivation — translate a character's equipped items into the
//  EquippedMythState shape the engine consumes.
//
//  An equipped item counts as "Myth-track" iff its item-level >= the
//  Myth threshold derived from the loot cache (the lowest ilvl across
//  cache items — by definition every cache item IS Myth-track, so the
//  floor is the bar).
//
//  Group-level "satisfied" requires ALL `positions` in the group to be
//  filled at Myth (RING/TRINKET need both). The engine then filters
//  catalyzed BiS targets out of those satisfied groups, while specific
//  BiS items keep their own per-itemID override.
// ──────────────────────────────────────────────────────────────────────────

import type { EquippedMythState, GroupID, LootCache } from "./types";
import { BIS_SLOTS, GROUP_BY_ID } from "./bis-slots";

/** One row from /api/characters/{id}/equipment, post-normalization. */
export interface EquippedItem {
  /** Blizzard slot.type: HEAD, NECK, FINGER_1, TRINKET_2, MAIN_HAND, … */
  slot: string;
  itemID: number;
  ilvl: number;
  name?: string | null;
  /** Blizzard's `name_description.display_string` — the authoritative
   *  difficulty / track marker. "Mythic", "Heroic", "Normal", "LFR",
   *  or keystone-track strings. May be null for crafted / world drops. */
  nameDescription?: string | null;
  /** Blizzard's quality enum (EPIC, RARE, …) — currently unused by the
   *  classifier but kept for future use (e.g. "show only epics"). */
  quality?: string | null;
}

/** Blizzard equipment-slot key → BiS group. Two-position groups
 *  (RING / TRINKET) intentionally collapse FINGER_1+FINGER_2 → RING. */
const SLOT_TO_GROUP: Record<string, GroupID> = {
  HEAD: "HEAD",
  NECK: "NECK",
  SHOULDER: "SHOULDER",
  BACK: "BACK",
  CHEST: "CHEST",
  WAIST: "BELT",
  LEGS: "LEGS",
  FEET: "FEET",
  WRIST: "WRIST",
  HANDS: "HANDS",
  FINGER_1: "RING",
  FINGER_2: "RING",
  TRINKET_1: "TRINKET",
  TRINKET_2: "TRINKET",
  MAIN_HAND: "MAINHAND",
  OFF_HAND: "OFFHAND",
};

export function groupForBlizzardSlot(slot: string): GroupID | undefined {
  return SLOT_TO_GROUP[slot.toUpperCase()];
}

/** Lowest ilvl across all cache items. Every cache item is Myth-track,
 *  so this is the bar a player's equipped piece must clear to count.
 *
 *  Falls back to 626 (Midnight S1 M+ Myth keystone baseline) when the
 *  cache is empty — keeps behaviour sane during the initial mock-data
 *  bootstrap. */
export function mythThreshold(cache: LootCache): number {
  let min = Infinity;
  for (const item of Object.values(cache.items)) {
    if (item.itemLevel < min) min = item.itemLevel;
  }
  return Number.isFinite(min) ? min : 626;
}

/** Derive `EquippedMythState` from a character's live equipment.
 *
 *  Two outputs with DIFFERENT semantics:
 *
 *    - `mythItemIDs`: every equipped item ID, regardless of track.
 *      The engine uses this to skip *specific* BiS items the user
 *      already owns — bonus-rolling for a duplicate is a wasted
 *      time-gated currency. (Naming kept for state-shape compat;
 *      think of it as "ownedItemIDs".)
 *
 *    - `satisfiedGroups`: BiS groups whose required slots are all
 *      already at the character's *own* Myth tier. We use the
 *      ratio-banded classifyTrack so cross-expansion characters
 *      (MoP Classic gear vs. a Midnight cache) get sensible answers
 *      based on their own gear distribution rather than an absolute
 *      ilvl floor. RING/TRINKET need 2; everything else 1.
 *
 *  The `cache` parameter is now unused but kept in the signature for
 *  call-site compat — every existing caller already passes it. */
export function deriveEquippedMyth(
  equipped: EquippedItem[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  cache: LootCache,
): EquippedMythState {
  // The character's own max equipped ilvl is the anchor for the
  // ratio-banded track classifier. Same anchor is used for satisfaction
  // counting AND mythItemIDs membership below — both depend on
  // "is this piece at Myth track for THIS character".
  const max = maxEquippedIlvl(equipped);

  // mythItemIDs: item IDs the user owns AT MYTH TRACK. The engine
  // uses this in `specific` BiS mode to skip an item ONLY when the
  // user already has the Myth-track version of that exact item —
  // because the same item ID at Myth is what the bonus roll would
  // give them. Owning the same item ID at a LOWER track (Hero,
  // Champion, Crafted) does NOT trigger a skip: the bonus roll's
  // Myth-track drop is a real upgrade.
  //
  // Example: the user's Cloak of the Fallen Cardinal (Hero, ilvl 276)
  // shares its item ID with the Myth-track version in the loot pool.
  // Skipping the slot just because the IDs match would block a
  // legitimate upgrade. So we gate by track.
  const mythItemIDs: number[] = [];

  // satisfiedGroups: BiS groups whose required slots are all already
  // at Myth. Only `myth` track counts — Crafted gear, even when its
  // ilvl is competitive, does NOT auto-skip the slot (a Myth-track
  // bonus roll is still an upgrade over Crafted in many cases:
  // different stats, set-bonus eligibility, sockets, tier).
  // RING / TRINKET (positions=2): BOTH slots must be Myth.
  const mythCountByGroup: Partial<Record<GroupID, number>> = {};

  for (const eq of equipped) {
    if (eq.itemID <= 0 || eq.ilvl <= 5) continue;  // skip shirt/tabard placeholders
    const track = classifyTrack(eq, max);
    if (track !== "myth") continue;
    // At Myth track:
    mythItemIDs.push(eq.itemID);
    const group = groupForBlizzardSlot(eq.slot);
    if (group) {
      mythCountByGroup[group] = (mythCountByGroup[group] ?? 0) + 1;
    }
  }

  const satisfiedGroups: Partial<Record<GroupID, true>> = {};
  for (const def of BIS_SLOTS) {
    const need = def.positions;
    const have = mythCountByGroup[def.id] ?? 0;
    if (have >= need) satisfiedGroups[def.id] = true;
  }

  return {
    satisfiedGroups,
    mythItemIDs: Array.from(new Set(mythItemIDs)).sort((a, b) => a - b),
  };
}

// ── Track classification (for the dashboard "Your gear" panel) ─────────
//
//  WoW's UPGRADE TRACK (Veteran / Champion / Hero / Myth) is the four-
//  rung ladder a piece of gear sits on, INDEPENDENT of where it dropped.
//  An item from Mythic+ might be on Hero or Myth track depending on
//  key level; a Mythic raid drop is always Myth; a Heroic raid drop is
//  Hero; etc. SOURCE ≠ TRACK.
//
//  Blizzard's public equipment endpoint tells us SOURCE in
//  `name_description.display_string` ("Mythic+", "Radiance Crafted",
//  "Heroic", …) but does NOT directly expose the upgrade track. The
//  track is encoded in `bonus_list` IDs which require an undocumented
//  lookup table. So we derive track from ilvl bands relative to the
//  CHARACTER'S OWN max equipped ilvl — this auto-calibrates per WoW
//  version (TWW S2, Midnight S1, MoP Classic Remix, …) and matches
//  the player's lived experience of "what's high vs. low for this char."
//
//  Bands (% of max equipped ilvl):
//    Myth    : ≥ 97 %   — top ~6 ilvls (Myth 1/6 .. 6/6)
//    Hero    : ≥ 93 %   — next ~12     (Hero 1/6 .. 6/6)
//    Champion: ≥ 89 %   — next ~12     (Champion 1/8 .. 8/8)
//    Veteran : <  89 %  — anything below
//
//  Verified against a real MoP Classic character (max ilvl 289):
//  trinket at 263 → Champion ✓, Mythic+ items at 276 → Hero ✓,
//  set chest at 289 → Myth ✓.
//
//  Crafted gear is its own thing — Blizzard tags it with strings like
//  "Radiance Crafted" / "Crafted" in `name_description`. We surface it
//  as a 5th display category (yellow) so the user can see at a glance
//  which slots are crafted vs. drop-track.

export type Track = "crafted" | "myth" | "hero" | "champion" | "veteran";

/** Crafted detection runs FIRST and overrides ilvl bands. */
function isCrafted(s: string | null | undefined): boolean {
  if (!s) return false;
  return s.toLowerCase().includes("crafted");
}

/** Match a retail keystone-track string ("Myth 6/6", "Champion 4/8", …)
 *  against the four tracks. Returns null on no match — caller falls
 *  back to ilvl banding. We do NOT match raid-difficulty strings or
 *  "Mythic+" here because those describe the SOURCE, not the track. */
function trackFromKeystoneString(s: string | null | undefined): Track | null {
  if (!s) return null;
  const head = s.trim().toLowerCase().split(/\s+/, 1)[0] ?? "";
  // Only the bare keystone-track names. "mythic" / "mythic+" / "heroic"
  // / "lfr" / "normal" intentionally fall through — those are sources,
  // and a Mythic+ drop can be on Champion track at low keys.
  if (head === "myth") return "myth";
  if (head === "hero") return "hero";
  if (head === "champion") return "champion";
  if (head === "veteran") return "veteran";
  return null;
}

/** Classify a single item's track. The caller must supply the
 *  character's max equipped ilvl — that's the calibration anchor.
 *
 *  - Crafted items short-circuit to "crafted" (yellow display badge).
 *  - Items with a literal keystone-track string use that.
 *  - Everything else is bucketed by ilvl relative to maxIlvl.
 */
export function classifyTrack(
  item: EquippedItem,
  maxIlvl: number,
): Track {
  if (isCrafted(item.nameDescription)) return "crafted";

  const fromKeystone = trackFromKeystoneString(item.nameDescription);
  if (fromKeystone) return fromKeystone;

  // Ratio-based banding — auto-calibrates across WoW versions.
  if (maxIlvl <= 0) return "veteran";
  const ratio = item.ilvl / maxIlvl;
  if (ratio >= 0.97) return "myth";
  if (ratio >= 0.93) return "hero";
  if (ratio >= 0.89) return "champion";
  return "veteran";
}

/** Compute the max equipped ilvl, ignoring placeholders (shirt / tabard
 *  often come back as ilvl 1). Returns 0 if there's nothing to anchor on. */
export function maxEquippedIlvl(equipped: EquippedItem[]): number {
  let max = 0;
  for (const it of equipped) {
    if (it.ilvl > max && it.ilvl > 5) max = it.ilvl;
  }
  return max;
}

/** Display info for a track — label + CSS color variable from globals.css.
 *  Crafted = yellow (warning color); Myth/Hero/Champion mirror raid
 *  difficulty colors (mythic purple, heroic blue, normal green);
 *  Veteran is a muted gray. */
export const TRACK_INFO: Record<Track, { label: string; colorVar: string }> = {
  myth:     { label: "Myth",     colorVar: "var(--color-diff-mythic)" },
  hero:     { label: "Hero",     colorVar: "var(--color-diff-heroic)" },
  champion: { label: "Champion", colorVar: "var(--color-diff-normal)" },
  crafted:  { label: "Crafted",  colorVar: "var(--color-warning)" },
  veteran:  { label: "Veteran",  colorVar: "var(--color-fg-subtle)" },
};

/** Re-export for tests that want to spell-check the group table. */
export { GROUP_BY_ID };
